// components/sections/ServicesAccordion.tsx
'use client';
import Link from 'next/link';
import {useState} from 'react';
import {ServicesAccordionProps} from '@/shared/pages/servicesShared';
import {consts} from "@/shared/commonShared";
import ExportedImage from "next-image-export-optimizer";

export default function ServicesAccordion({ data }: ServicesAccordionProps) {
	// Set the default active tab to the ID of the first tab in the array
	const [activeTab, setActiveTab] = useState(data.tabs[0].id);

	if (!data || !data.tabs || data.tabs.length === 0) return null;
	return (
		<>
			<section id={data.id} className={"section-services-4 bg-3 position-relative fix py-"+consts.PADDING}>
				<div className="container position-relative z-2">
					<div className="text-center">
						<div
							className="d-flex align-items-center justify-content-center bg-primary-soft border border-2 border-white d-inline-flex rounded-pill px-4 py-2"
							data-aos={data.animated ? "zoom-in" : ""}
							data-aos-delay={100}
						>
							<ExportedImage
								src="/assets/imgs/dots.png"
								alt="decorative dots"
								width={24}
								height={24}
							/>
							<span
								className="tag-spacing fs-7 fw-bold text-linear-2 ms-2 text-uppercase"
								dangerouslySetInnerHTML={{ __html: data.tag }}
							/>
						</div>
						<h3
							className={"ds-3 fw-regular my-"+consts.PADDING_SMALL}
							dangerouslySetInnerHTML={{ __html: data.title }}
						/>
					</div>
					<div className="row">
						<div className={"col-lg-4 p-0 pe-lg-"+consts.PADDING_SMALL}>
							<div className="d-flex align-items-start">
								<div
									className="nav w-100 flex-column nav-pills"
									id="v-pills-tab"
									role="tablist"
									aria-orientation="vertical"
								>
									{/* --- DYNAMIC NAV BUTTONS --- */}
									{data.tabs.map((tab, index) => (
										<button
											// Use state to set the 'active' class
											className={`${
												activeTab === tab.id ? 'active' : ''
											} nav-link pe-5 justify-content-between d-flex mb-0 mt-`+((index==0) ? 0 : consts.PADDING_SMALL)}
											// Use state to manage the click
											onClick={() => setActiveTab(tab.id)}
											id={`v-pills-${tab.id}-tab`}
											type="button"
											role="tab"
											aria-controls={`v-pills-${tab.id}`}
											aria-selected={activeTab === tab.id}
											key={tab.id}
										>
											{tab.navTitle}
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width={24}
												height={24}
												viewBox="0 0 24 24"
												fill="none"
											>
												<path
													className="fill-white"
													d="M17.4177 5.41772L16.3487 6.48681L21.1059 11.244H0V12.756H21.1059L16.3487 17.5132L17.4177 18.5822L24 12L17.4177 5.41772Z"
													fill="white"
												/>
											</svg>
										</button>
									))}
								</div>
							</div>
						</div>
						<div className={"col-lg-8 mt-lg-0 shadow-2 border mt-"+consts.PADDING_SMALL}>
							<div className="tab-content h-100" id="v-pills-tabContent">
								{/* --- DYNAMIC TAB PANES --- */}
								{data.tabs.map((tab) => (
									<div
										// Use state to set 'show' and 'active' classes
										className={`${
											activeTab === tab.id ? 'h-100 show active' : ''
										} tab-pane fade`}
										id={`v-pills-${tab.id}`}
										role="tabpanel"
										aria-labelledby={`v-pills-${tab.id}-tab`}
										tabIndex={0}
										key={tab.id}
									>
										<div className={"row h-100 align-items-center rounded-3 bg-white p-"+consts.PADDING+" p-lg-"+consts.PADDING_SMALL}>
											<div className="col-lg-4 mb-lg-0 mb-5 order-2 order-lg-1 text-center">
												<ExportedImage
													src={tab.content.image}
													alt={tab.navTitle}
													width={600}
													height={800}
													className="about-main-image rounded-4 position-relative z-1 img-fluid"
													sizes="(max-width: 992px) 100vw, 50vw"
												/>
											</div>
											<div className="col-lg-8 order-1 order-lg-2">
												<div className="p-lg-1">

													{/* Dynamic Features */}
													{tab.content.features.map((feature) => (
														<div
															className="d-flex align-items-start gap-2"
															key={feature.id}
														>
															<div className="fs-7">
																<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
																	<path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"></path>
																</svg>
															</div>
															<div className="">
																<h6 className="text-capitalize">{feature.title}</h6>
																<p
																	dangerouslySetInnerHTML={{
																		__html: feature.description,
																	}}
																/>
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
					<div className={"d-flex flex-column gap-2 d-sm-block text-center mt-"+consts.PADDING}>
						<Link href="/contact" className="btn btn-gradient fs-6 me-sm-2">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={24}
								height={24}
								viewBox="0 0 26 26"
								fill="none"
							>
								<path
									className="stroke-white"
									d="M8.89286 4.75H6.06818C5.34017 4.75 4.75 5.34017 4.75 6.06818C4.75 13.3483 10.6517 19.25 17.9318 19.25C18.6598 19.25 19.25 18.6598 19.25 17.9318V15.1071L16.1429 13.0357L14.5317 14.6468C14.2519 14.9267 13.8337 15.0137 13.4821 14.8321C12.8858 14.524 11.9181 13.9452 10.9643 13.0357C9.98768 12.1045 9.41548 11.1011 9.12829 10.494C8.96734 10.1537 9.06052 9.76091 9.32669 9.49474L10.9643 7.85714L8.89286 4.75Z"
									stroke="white"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Get in touch
							<svg
								className="ms-2"
								xmlns="http://www.w3.org/2000/svg"
								width={24}
								height={24}
								viewBox="0 0 24 24"
								fill="none"
							>
								<path
									className="stroke-white"
									d="M17.25 15.25V6.75H8.75"
									stroke="white"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									className="stroke-white"
									d="M17 7L6.75 17.25"
									stroke="white"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</Link>
					</div>
				</div>
				<div className="position-absolute top-0 start-50 translate-middle-x z-0">
					<ExportedImage width={1000} height={500} src="/assets/imgs/blog/img-bg-line.png" alt="infinia" />
				</div>

				<div className="rotate-center ellipse-rotate-success position-absolute z-1" />
				<div className="rotate-center-rev ellipse-rotate-primary position-absolute z-1" />
			</section>
		</>
	);
}